import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { compareFamily, normalizedText } from '@domain/product'
import type { PricePoint, Product, ProductFamily } from '@domain/product'
import { Glass } from '@ui/glass/Glass'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { PilhaDeFotos } from './PilhaDeFotos'
import { ReguaDeFamilia } from './ReguaDeFamilia'

/**
 * A lista de famílias — os grupos de embalagem.
 *
 * ## Ela responde uma pergunta que a lista de produtos não responde
 *
 * A lista de produtos é a lista de **embalagens**: "Coca-Cola 2L", "Coca-Cola
 * lata". A de famílias é a lista de **coisas que se compra**, e é onde se vê,
 * sem abrir nada, qual tamanho está compensando em cada uma.
 *
 * ## Produto solto é uma família de um (decisão 202)
 *
 * Toda linha tem a mesma anatomia; o que muda é quantas embalagens preenchem a
 * régua. Não existe seção "sem família" — ela reexporia o modelo de dados que
 * esta tela existe para esconder, e faria o catálogo parecer dois catálogos.
 *
 * Uma família de um mostra a régua com uma barra só: é honesto — não há com o
 * que comparar — e é o convite pelo tamanho que falta.
 *
 * ## Carrega o histórico de TODAS as famílias, e por que isso é aceitável
 *
 * Uma leitura por variação. Medido na base real, o maior mês tem 91 transações
 * e o catálogo é de dezenas de produtos — a conta é de dezenas de leituras
 * pequenas, e a alternativa seria guardar um resumo por família, que é uma
 * segunda fonte de verdade sobre preço. Reabre quando o catálogo passar das
 * centenas.
 */
export function Familias({
  versao = 0,
}: {
  /**
   * Muda quando a tela grava um produto, e a lista relê. Ela lia só ao
   * montar, e o produto recém-cadastrado só aparecia depois de trocar de aba
   * e voltar (pedido do dono, decisão 316). Relê sem voltar ao "Carregando…":
   * a lista antiga fica na tela até a nova chegar, e a busca digitada fica.
   */
  versao?: number
} = {}) {
  const { productRepository } = useFinanceRepositories()

  const [dados, setDados] = useState<
    | { status: 'carregando' }
    | { status: 'falhou' }
    | {
        status: 'pronto'
        familias: ProductFamily[]
        produtos: Product[]
        pontos: Map<string, PricePoint[]>
      }
  >({ status: 'carregando' })
  const [busca, setBusca] = useState('')

  useEffect(() => {
    let vivo = true
    void Promise.all([productRepository.listFamilies(), productRepository.list()])
      .then(async ([familias, produtos]) => {
        // TODOS os ativos, e não só os que têm família: produto solto agora é
        // uma família de um, e sem o histórico dele a régua ficaria vazia
        // justamente na linha em que ela é a única informação.
        const ativos = produtos.filter((p) => p.archivedAt === null)
        const historicos = await Promise.all(
          ativos.map(async (p) => [p.id, await productRepository.priceHistory(p.id)] as const),
        )
        if (!vivo) return
        setDados({ status: 'pronto', familias, produtos, pontos: new Map(historicos) })
      })
      .catch((e: unknown) => {
        console.error('Familias: a leitura falhou', e)
        if (vivo) setDados({ status: 'falhou' })
      })
    return () => {
      vivo = false
    }
  }, [productRepository, versao])

  if (dados.status === 'carregando') {
    return <p className="text-sm text-[var(--text-muted)]">Carregando os grupos…</p>
  }
  if (dados.status === 'falhou') {
    return <p className="text-sm">Não foi possível carregar os grupos.</p>
  }

  // A busca passa pela MESMA normalização do catálogo: "coca" acha "Coca-Cola".
  const termo = normalizedText(busca)
  const ativos = dados.produtos.filter((p) => p.archivedAt === null)

  /**
   * As linhas da tela: uma por família, mais uma por produto sem família.
   *
   * As duas têm a mesma forma de propósito — `nome`, `variantes`, e o destino.
   * Uma linha que se comportasse diferente por causa de um campo nulo faria a
   * pessoa aprender o modelo de dados para usar a lista.
   */
  const linhas = [
    ...dados.familias
      .filter((f) => f.archivedAt === null)
      .map((f) => ({
        chave: `fam:${f.id}`,
        nome: f.name,
        chave_busca: f.normalizedName,
        variantes: ativos.filter((p) => p.familyId === f.id),
        destino: { to: '/grupos/$id' as const, params: { id: f.id } },
      })),
    ...ativos
      .filter((p) => p.familyId === null)
      .map((p) => ({
        chave: `prod:${p.id}`,
        nome: p.name,
        chave_busca: p.normalizedName,
        variantes: [p],
        destino: { to: '/produtos/$id' as const, params: { id: p.id } },
      })),
  ]
    .filter((l) => l.variantes.length > 0)
    .filter((l) => termo === '' || l.chave_busca.includes(termo))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))

  return (
    <Glass level="frost" className="p-5" as="section" aria-label="Grupos">
      <label className="mb-3 block">
        <span className="sr-only">Buscar grupo</span>
        <input
          type="search"
          data-atalho-busca
          value={busca}
          placeholder="Buscar"
          className="w-full rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-sm"
          onChange={(e) => {
            setBusca(e.target.value)
          }}
        />
      </label>

      {ativos.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">
          Nenhum produto ainda. Cadastre um e registre o que ele custa — quando houver dois
          tamanhos da mesma coisa, esta tela diz qual compensa por litro.
        </p>
      ) : linhas.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">Nada com esse nome.</p>
      ) : (
        <ul aria-label="Grupos">
          {linhas.map((linha) => {
            const comparacao = compareFamily(linha.variantes, dados.pontos)
            return (
              <li
                key={linha.chave}
                className="flex items-stretch gap-3 border-b border-[var(--border)] py-3 text-sm"
              >
                {/* O baralho é IRMÃO da coluna de texto, e não parte da mesma
                    linha do nome. Antes ele vivia dentro do `<Link>` do título
                    e o `<details>` ficava abaixo dos dois — o que deixava a
                    coluna das fotos com a altura só do nome, e um vão morto ao
                    lado de "Ver o preço". Agora ela ocupa a linha inteira, e a
                    carta pode crescer nela.

                    Continua sendo link: tocar na foto de um grupo é querer o
                    grupo, e obrigar a mirar o nome seria um alvo menor.

                    **`aria-hidden` + `tabIndex={-1}`**: ele leva ao mesmo lugar
                    que o link do nome, ao lado. Sem isso o leitor de tela
                    anuncia o mesmo destino duas vezes e o Tab para duas vezes
                    na mesma linha — o padrão para link redundante adjacente é
                    deixar um só na árvore de acessibilidade. Quem toca com o
                    dedo continua com os dois. */}
                <Link
                  {...linha.destino}
                  aria-hidden="true"
                  tabIndex={-1}
                  className="flex w-1/3 shrink-0 items-center"
                >
                  <PilhaDeFotos variantes={linha.variantes} nome={linha.nome} />
                </Link>

                <span className="flex min-w-0 flex-1 flex-col justify-center">
                  <Link {...linha.destino} data-nav-linha className="min-w-0">
                    <span className="block truncate font-medium underline">{linha.nome}</span>
                    <span className="mt-0.5 block text-xs text-[var(--text-muted)]">
                      {linha.variantes.length === 1
                        ? '1 embalagem'
                        : `${String(linha.variantes.length)} embalagens`}
                    </span>
                  </Link>

                  {/*
                    **A comparação é um item colapsável, fechado por padrão
                    (decisão 226).**

                    Isto REVERTE a tese que este arquivo declarava: *"a resposta
                    na própria linha, sem abrir nada"*. Ela valia quando a linha
                    era compacta; com o baralho ocupando um terço, cada grupo
                    passou a ocupar meia tela, e quinze grupos viravam uma
                    parede que ninguém varre.

                    Renderizado SEMPRE, inclusive com uma embalagem só: produto
                    solto entra nesta lista como grupo de um (decisão 202), e
                    escondê-lo por não haver o que comparar deixaria a linha sem
                    preço nenhum. O rótulo é que muda.
                  */}
                  <details className="group mt-1">
                    <summary className="flex cursor-pointer list-none items-center gap-1.5 py-1 text-xs text-[var(--text-muted)]">
                      <span
                        aria-hidden="true"
                        className="inline-block transition-transform group-open:rotate-90"
                      >
                        ›
                      </span>
                      {linha.variantes.length === 1 ? 'Ver o preço' : 'Comparar tamanhos'}
                    </summary>
                    <ReguaDeFamilia comparacao={comparacao} className="mt-1.5" />
                  </details>
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </Glass>
  )
}
